import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, ReactNode, useRef } from 'react';
import { useAuth } from './AuthContext';
import { calculateShippingCost } from '@/config/cartConfig';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { logger } from '@/utils/logger';
import { analytics, ANALYTICS_EVENT, ANALYTICS_CATEGORY } from '@/services/analyticsService';

export interface ShippingRate {
  providerId: string;
  providerName: string;
  serviceType: string;
  rate: number;
  estimatedDeliveryDays: number;
  codCharges?: number;
  buyerPincode?: string;
  sellerPincode?: string;
  /** 'live' = real courier quote; 'fallback' = local estimate (never trust for long) */
  source?: 'live' | 'fallback';
  /** Epoch ms when this rate was fetched; used for TTL expiry */
  fetchedAt?: number;
  /**
   * Set when the payment server has already confirmed this exact charge.
   * Pinned rates are reused as-is (even fallback estimates) so a reactive
   * re-quote cannot overwrite the figure the buyer is about to pay.
   */
  pinned?: boolean;
  /** Why this rate is an estimate, when known (server-provided) */
  fallbackReason?: string;
}

/** How long a cached live rate is trusted before re-fetching (courier pricing can drift) */
const SHIPPING_RATE_TTL_MS = 5 * 60 * 1000;

/**
 * A cached rate is reusable only if it is a fresh real quote, OR a
 * payment-server-confirmed (pinned) charge for the same destination.
 */
const isReusableRate = (rate: ShippingRate | undefined, buyerPincode: string): boolean => {
  if (!rate || rate.buyerPincode !== buyerPincode) return false;
  if (!rate.fetchedAt || Date.now() - rate.fetchedAt > SHIPPING_RATE_TTL_MS) return false;
  if (rate.pinned) return true;
  return rate.source === 'live';
};


// Note: No quantity field - each used book is unique, quantity is always 1
export interface CartItem {
  id: string;
  title: string;
  author: string;
  price: number;
  condition: string;
  image: string;
  seller_id?: string;
  sellerPincode?: string;
}

// Group items by seller for shipping calculation
export interface SellerGroup {
  sellerId: string;
  sellerPincode: string;
  items: CartItem[];
  shippingRate?: ShippingRate;
}

/** Why a displayed shipping charge is an estimate rather than a real courier quote */
export type ShippingEstimateReason = 'courier_fallback' | 'seller_rate_missing';

export interface ResolvedShippingCost {
  cost: number;
  /** true when `cost` is a locally computed guess, not a courier quote */
  isEstimate: boolean;
  reason?: ShippingEstimateReason;
}

/**
 * Single source of truth for "what shipping do we show for this seller".
 * Every guess — courier fallback OR no rate on record — is tagged as an estimate
 * so the UI can warn and logs can record it. No silent substitution anywhere.
 */
export const resolveSellerShippingCost = (
  group: SellerGroup,
  buyerPincode?: string,
): ResolvedShippingCost => {
  const rate = group.shippingRate?.rate;
  // PROVISIONAL: `rate > 0` discards real quotes of ₹0 because the current
  // staging Delhivery token returns dummy ₹0 rates (test-environment noise).
  // With a production token a genuine ₹0 route would be mislabelled as
  // 'seller_rate_missing'. TODO: once the production token is in, treat a
  // finite `source: 'live'` ₹0 as a real quote instead of falling through.
  if (typeof rate === 'number' && Number.isFinite(rate) && rate > 0) {
    const isEstimate = group.shippingRate?.source === 'fallback';
    return { cost: rate, isEstimate, reason: isEstimate ? 'courier_fallback' : undefined };
  }
  return {
    // Same formula (base + slabs + route multiplier) the payment server uses,
    // so the page estimate and the payment-time estimate agree for identical
    // inputs instead of the page quoting a lower number.
    cost: calculateShippingCost(
      group.items.length,
      group.sellerPincode,
      buyerPincode ?? group.shippingRate?.buyerPincode,
    ),
    isEstimate: true,
    reason: 'seller_rate_missing',
  };
};


export interface ShippingEstimateNotice {
  sellerId: string;
  reason: ShippingEstimateReason;
  cost: number;
}

interface CartContextType {
  cartItems: CartItem[];
  addToCart: (item: CartItem) => Promise<boolean>;
  removeFromCart: (id: string) => void;
  updateSellerShippingRate: (sellerId: string, shippingRate: ShippingRate) => void;
  /** Resolves true when a usable rate is in place, false when the quote failed. */
  calculateSellerShippingRate: (group: SellerGroup, buyerPincode: string, forceRefresh?: boolean) => Promise<boolean>;
  clearStaleShippingRates: (newBuyerPincode: string) => void;
  clearCart: () => void;
  getTotalItems: () => number;
  getTotalPrice: () => number;
  getTotalShipping: () => number;
  getSellerGroups: () => SellerGroup[];
  /** Sellers whose displayed shipping charge is an estimate, with the reason */
  getShippingEstimates: () => ShippingEstimateNotice[];
  sellerShippingRates: Record<string, ShippingRate>;
  shippingLoadingSellers: Set<string>;
  /** Last known destination pincode; used so estimates include the route multiplier */
  buyerPincode: string;
  /** Resolved (and labelled) shipping charge for a seller group */
  resolveShippingFor: (group: SellerGroup) => ResolvedShippingCost;

}

const CartContext = createContext<CartContextType | undefined>(undefined);

const getCartKey = (userId: string | null) => {
  return userId ? `cart_${userId}` : 'cart_guest';
};

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [sellerShippingRates, setSellerShippingRates] = useState<Record<string, ShippingRate>>({});
  const [shippingLoadingSellers, setShippingLoadingSellers] = useState<Set<string>>(new Set());
  // Last known destination pincode, so locally computed estimates can apply the
  // same route multiplier the payment server uses.
  const [buyerPincode, setBuyerPincode] = useState<string>('');
  const calculatingRef = useRef<Set<string>>(new Set());


  const prevUserIdRef = useRef<string | null>(null);

  // Load cart from localStorage when user changes; merge guest cart on login
  useEffect(() => {
    const newUserId = user?.id || null;
    const prevUserId = prevUserIdRef.current;
    const cartKey = getCartKey(newUserId);
    const savedCart = localStorage.getItem(cartKey);
    const savedShippingRates = localStorage.getItem(`${cartKey}_shipping`);

    let initialItems: CartItem[] = [];
    try {
      initialItems = savedCart ? JSON.parse(savedCart) : [];
    } catch {
      initialItems = [];
    }

    let mergedShippingFromGuest: Record<string, ShippingRate> | null = null;
    const reservedGuestIds: string[] = [];

    // On guest -> logged-in transition, merge guest cart + shipping into user cart
    if (!prevUserId && newUserId) {
      try {
        const guestCart = localStorage.getItem('cart_guest');
        const guestItems: CartItem[] = guestCart ? JSON.parse(guestCart) : [];
        for (const gi of guestItems) {
          if (!initialItems.find(i => i.id === gi.id)) {
            initialItems.push(gi);
            reservedGuestIds.push(gi.id);
          }
        }

        const guestShippingRaw = localStorage.getItem('cart_guest_shipping');
        const guestShipping: Record<string, ShippingRate> = guestShippingRaw
          ? JSON.parse(guestShippingRaw)
          : {};

        if (Object.keys(guestShipping).length > 0) {
          // Merge: user-side rates take precedence over guest if both exist
          const userShipping: Record<string, ShippingRate> = savedShippingRates
            ? JSON.parse(savedShippingRates)
            : {};
          mergedShippingFromGuest = { ...guestShipping, ...userShipping };
        }

        if (guestItems.length > 0 || Object.keys(guestShipping).length > 0) {
          localStorage.removeItem('cart_guest');
          localStorage.removeItem('cart_guest_shipping');
        }
      } catch { /* ignore */ }
    }


    setCartItems(initialItems);

    if (mergedShippingFromGuest) {
      setSellerShippingRates(mergedShippingFromGuest);
    } else if (savedShippingRates) {
      try {
        setSellerShippingRates(JSON.parse(savedShippingRates));
      } catch {
        setSellerShippingRates({});
      }
    } else {
      setSellerShippingRates({});
    }

    // P1-11: best-effort reservation for items merged from the guest cart on
    // sign-in. Guests cannot reserve while browsing, so we do it now that
    // they're authenticated. Failures are non-blocking — useCartValidation
    // surfaces sold/price-changed issues at checkout regardless.
    if (newUserId && reservedGuestIds.length > 0) {
      void (async () => {
        for (const bookId of reservedGuestIds) {
          try {
            await supabase.rpc('reserve_book_for_cart', { p_book_id: bookId });
          } catch (e) {
            logger.debug('[Cart] guest-merge reservation failed', { bookId, e });
          }
        }
      })();
    }

    prevUserIdRef.current = newUserId;
  }, [user?.id]);

  // Defensive cleanup: remove any items the current user is the seller of.
  // Covers the edge case where a buyer added a book to cart before becoming
  // the seller, or where stale localStorage data contains own listings.
  useEffect(() => {
    if (!user?.id) return;
    setCartItems(items => items.filter(i => !i.seller_id || i.seller_id !== user.id));
  }, [user?.id]);

  // Save cart to localStorage whenever cartItems changes
  useEffect(() => {
    try {
      const cartKey = getCartKey(user?.id || null);
      if (cartItems.length === 0) {
        localStorage.removeItem(cartKey);
      } else {
        localStorage.setItem(cartKey, JSON.stringify(cartItems));
      }
    } catch {
      // Storage unavailable (e.g., Safari private mode)
    }
  }, [cartItems, user?.id]);

  // Save seller shipping rates to localStorage
  useEffect(() => {
    try {
      const cartKey = getCartKey(user?.id || null);
      localStorage.setItem(`${cartKey}_shipping`, JSON.stringify(sellerShippingRates));
    } catch {
      // Storage unavailable
    }
  }, [sellerShippingRates, user?.id]);

  const addToCart = async (newItem: CartItem): Promise<boolean> => {
    // Block sellers from purchasing their own listings
    if (user?.id && newItem.seller_id && newItem.seller_id === user.id) {
      toast.error('You cannot purchase your own listing');
      return false;
    }
    // If already in user's cart, surface info toast and bail
    if (cartItems.some(item => item.id === newItem.id)) {
      toast.info('This book is already in your cart');
      return false;
    }

    // Try to reserve the book server-side (30-min hold) when user is logged in.
    // Guests cannot reserve; reservation is enforced again at checkout.
    if (user?.id) {
      try {
        const { data, error } = await supabase.rpc('reserve_book_for_cart', {
          p_book_id: newItem.id,
        });
        if (error) {
          logger.error('[Cart] reserve_book_for_cart failed', error);
          toast.error('Could not add to cart. Please try again.');
          return false;
        }
        const result = data as { success?: boolean; error?: string; expires_at?: string } | null;
        if (result && result.success === false) {
          if (result.error === 'reserved_by_other') {
            toast.error('Currently in another buyer’s cart', {
              description: 'Try again later — the hold expires within 30 minutes.',
            });
            return false;
          }
          toast.error('Could not add to cart. Please try again.');
          return false;
        }
      } catch (e) {
        logger.error('[Cart] reservation exception', e);
        toast.error('Could not add to cart. Please try again.');
        return false;
      }
    }

    setCartItems(items => (items.some(i => i.id === newItem.id) ? items : [...items, newItem]));
    // Cart composition changed → the seller's shipment weight changed, so any
    // saved rate (pinned server figure or live quote) was computed under
    // conditions that no longer hold. Drop it and let a fresh quote happen.
    // Without this, checkout could show an old total until payment-time
    // validation rejected it — the same last-second-jump bug as the
    // pincode-change case.
    if (newItem.seller_id) {
      setSellerShippingRates(prev => {
        if (!(newItem.seller_id! in prev)) return prev;
        const { [newItem.seller_id!]: _, ...rest } = prev;
        return rest;
      });
    }
    toast.success('Added to cart');
    analytics.trackEvent(ANALYTICS_EVENT.ADD_TO_CART, ANALYTICS_CATEGORY.ECOMMERCE, {
      book_id: newItem.id,
      seller_id: newItem.seller_id,
      price: newItem.price,
      currency: 'INR',
    }, newItem.price);
    return true;
  };


  const removeFromCart = (id: string) => {
    setCartItems(items => {
      const itemToRemove = items.find(item => item.id === id);
      const newItems = items.filter(item => item.id !== id);

      // Any change to a seller's item list changes the shipment weight, so
      // the saved rate (pinned or live) is stale — drop it on removal, not
      // only when the seller's last item leaves the cart.
      if (itemToRemove?.seller_id) {
        setSellerShippingRates(prev => {
          if (!(itemToRemove.seller_id! in prev)) return prev;
          const { [itemToRemove.seller_id!]: _, ...rest } = prev;
          return rest;
        });
      }

      return newItems;
    });

    // Release reservation server-side (best-effort)
    if (user?.id) {
      supabase.rpc('release_book_reservation', { p_book_id: id }).then(({ error }) => {
        if (error) logger.error('[Cart] release_book_reservation failed', error);
      });
    }
  };



  // Update shipping rate for a seller (all items from this seller share the rate)
  const updateSellerShippingRate = (sellerId: string, shippingRate: ShippingRate) => {
    setSellerShippingRates(prev => ({
      ...prev,
      [sellerId]: shippingRate
    }));
  };

  // Clear stale shipping rates when buyer pincode changes
  const clearStaleShippingRates = useCallback((newBuyerPincode: string) => {
    if (/^\d{6}$/.test(newBuyerPincode)) setBuyerPincode(newBuyerPincode);
    setSellerShippingRates(prev => {
      const updated: Record<string, ShippingRate> = {};
      let changed = false;
      for (const [sellerId, rate] of Object.entries(prev)) {
        if (rate.buyerPincode === newBuyerPincode) {
          updated[sellerId] = rate;
        } else {
          changed = true;
        }
      }
      return changed ? updated : prev;
    });
  }, []);

  // Centralized shipping rate calculation — called per seller group
  const calculateSellerShippingRate = useCallback(async (
    group: SellerGroup,
    buyerPincode: string,
    forceRefresh = false,
  ): Promise<boolean> => {
    if (!buyerPincode || buyerPincode.length !== 6 || !group.sellerPincode) return false;
    setBuyerPincode(buyerPincode);
    // Reuse only a fresh, real courier quote (or a rate pinned by the payment
    // server); fallback estimates and stale rates are re-fetched.
    if (!forceRefresh && isReusableRate(group.shippingRate, buyerPincode)) return true;
    if (calculatingRef.current.has(group.sellerId)) return true;

    calculatingRef.current.add(group.sellerId);
    setShippingLoadingSellers(prev => new Set(prev).add(group.sellerId));


    try {
      const totalWeight = Math.max(0.5, group.items.length * 0.5);
      const { data, error } = await supabase.functions.invoke('shipping-rates', {
        body: {
          fromPincode: group.sellerPincode,
          toPincode: buyerPincode,
          weight: totalWeight,
          paymentMode: 'Prepaid',
          forceRefresh,
        }
      });

      if (error) {
        logger.error('Rate calculation error:', error);
        toast.error('Error calculating shipping rate');
        return false;
      }

      if (Array.isArray(data?.rates) && data.rates.length > 0) {
        const bestRate = [...data.rates].sort((a: ShippingRate, b: ShippingRate) => a.rate - b.rate)[0] as ShippingRate;
        updateSellerShippingRate(group.sellerId, {
          ...bestRate,
          buyerPincode,
          sellerPincode: group.sellerPincode,
          fetchedAt: Date.now()
        });
        if (bestRate.source === 'fallback') {
          toast.warning('Live courier rates unavailable — showing an estimate. It will be re-checked automatically.');
        }
        return true;
      }

      toast.warning('No shipping options available');
      return false;
    } catch (error) {
      logger.error('Rate calculation failed:', error);
      toast.error('Failed to calculate shipping');
      return false;
    } finally {
      calculatingRef.current.delete(group.sellerId);
      setShippingLoadingSellers(prev => {
        const next = new Set(prev);
        next.delete(group.sellerId);
        return next;
      });
    }
  }, [updateSellerShippingRate]);

  const clearCart = () => {
    setCartItems([]);
    setSellerShippingRates({});
    // Also remove from localStorage immediately
    try {
      const cartKey = getCartKey(user?.id || null);
      localStorage.removeItem(cartKey);
      localStorage.removeItem(`${cartKey}_shipping`);
    } catch {
      // Storage unavailable
    }
    // Release all server-side reservations (best-effort)
    if (user?.id) {
      supabase.rpc('release_all_my_reservations').then(({ error }) => {
        if (error) logger.error('[Cart] release_all_my_reservations failed', error);
      });
    }
  };

  // Each book is unique, so total items = cart length (no quantity field needed)
  const totalItems = useMemo(() => cartItems.length, [cartItems]);
  const totalPrice = useMemo(() => {
    return cartItems.reduce((total, item) => total + item.price, 0);
  }, [cartItems]);

  // Group items by seller for shipping calculation
  const sellerGroups = useMemo((): SellerGroup[] => {
    const groups: Record<string, SellerGroup> = {};
    
    cartItems.forEach(item => {
      // Use item ID as fallback to prevent incorrect grouping of items without seller_id
      // Fallback prevents unrelated items without seller_id from being grouped together
      const sellerId = item.seller_id || `item_${item.id}`;
      if (!groups[sellerId]) {
        groups[sellerId] = {
          sellerId,
          sellerPincode: item.sellerPincode || '',
          items: [],
          shippingRate: sellerShippingRates[sellerId]
        };
      }
      groups[sellerId].items.push(item);
    });
    
    return Object.values(groups);
  }, [cartItems, sellerShippingRates]);

  /** Resolved shipping per seller — every guess is tagged, never silently substituted */
  const resolvedShipping = useMemo(
    () => sellerGroups.map(group => ({ sellerId: group.sellerId, ...resolveSellerShippingCost(group, buyerPincode) })),
    [sellerGroups, buyerPincode]
  );

  const resolveShippingFor = useCallback(
    (group: SellerGroup) => resolveSellerShippingCost(group, buyerPincode),
    [buyerPincode]
  );


  const totalShipping = useMemo(
    () => resolvedShipping.reduce((total, entry) => total + entry.cost, 0),
    [resolvedShipping]
  );

  const shippingEstimates = useMemo<ShippingEstimateNotice[]>(
    () =>
      resolvedShipping
        .filter(entry => entry.isEstimate && entry.reason)
        .map(entry => ({ sellerId: entry.sellerId, reason: entry.reason!, cost: entry.cost })),
    [resolvedShipping]
  );

  // Loud, structured record whenever a displayed charge is a guess
  useEffect(() => {
    shippingEstimates.forEach(entry => {
      logger.warn('[Cart] ESTIMATED_SHIPPING_DISPLAYED', {
        sellerId: entry.sellerId,
        reason: entry.reason,
        cost: entry.cost,
      });
    });
  }, [shippingEstimates]);

  // Getter functions for API compatibility
  const getTotalItems = useCallback(() => totalItems, [totalItems]);
  const getTotalPrice = useCallback(() => totalPrice, [totalPrice]);
  const getSellerGroups = useCallback(() => sellerGroups, [sellerGroups]);
  const getTotalShipping = useCallback(() => totalShipping, [totalShipping]);
  const getShippingEstimates = useCallback(() => shippingEstimates, [shippingEstimates]);

  const value = {
    cartItems,
    addToCart,
    removeFromCart,
    updateSellerShippingRate,
    clearCart,
    getTotalItems,
    getTotalPrice,
    getTotalShipping,
    getSellerGroups,
    getShippingEstimates,
    sellerShippingRates,
    calculateSellerShippingRate,
    clearStaleShippingRates,
    shippingLoadingSellers,
    buyerPincode,
    resolveShippingFor,
  };


  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
